import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DayView from "@/pages/DayView";
import MonthView from "@/pages/MonthView";
import YearView from "@/pages/YearView";
import Appointment from "@/pages/Appointment";
import History from "@/pages/History";
import Vehicles from "@/pages/Vehicles";
import VehiclesOtherBrands from "@/pages/VehiclesOtherBrands";
import VehicleProfile from "@/pages/VehicleProfile";
import VehicleForm from "@/pages/VehicleForm";
import Upcoming from "@/pages/Upcoming";
import NewAppointment from "@/pages/NewAppointment";
import AppointmentDetail from "@/pages/AppointmentDetail";
import SettingsOverview from "@/pages/SettingsOverview";
import OtherSettings from "@/pages/OtherSettings";
import Branches from "@/pages/Branches";
import Roles from "@/pages/Roles";
import UserManagement from "@/pages/UserManagement";
import ComingSoon from "@/pages/ComingSoon";
import Reception from "@/pages/Reception";
import DataQuality from "@/pages/DataQuality";
import InsurancePolicies from "@/pages/InsurancePolicies";
import Layout from "@/components/Layout";
import { NavGuardProvider } from "@/lib/navGuard";
import { TestModeProvider } from "@/components/TestEnvBanner";
import { can, canAnyVehicles, canCR } from "@/lib/permissions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Global axios 401 handler — bounces any stale/invalid session to /login once.
// Skips the /auth/* endpoints themselves so the login screen can show its own
// "Invalid credentials" toast without redirecting.
let _redirectingToLogin = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/logout");
      const onLoginPage = window.location.pathname.startsWith("/login");
      if (!isAuthEndpoint && !onLoginPage && !_redirectingToLogin) {
        _redirectingToLogin = true;
        // Use replace so back-button doesn't re-trigger the failing page
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialUser = location.state?.user || null;
  const [isAuthenticated, setIsAuthenticated] = useState(initialUser ? true : null);
  const [user, setUser] = useState(initialUser);

  useEffect(() => {
    if (initialUser) {
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    };

    checkAuth();
  }, [initialUser, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated === false) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Gates a route by permission page/level; redirects away if not allowed.
const RequirePermission = ({ page, level = "view", children }) => {
  const { user } = useAuth();
  if (!can(user, page, level)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Gates the dynamic /module/:module ComingSoon route by its matrix key.
const MODULE_PERM_KEYS = ["parts", "bodyshop", "mechanical", "insurance", "hr"];
const RequireModulePermission = ({ children }) => {
  const { user } = useAuth();
  const { module } = useParams();
  if (MODULE_PERM_KEYS.includes(module) && !can(user, module, "view")) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Gates the /vehicles/* routes: allowed if ANY Vehicles sub-page is viewable.
const RequireVehicles = ({ children }) => {
  const { user } = useAuth();
  if (!canAnyVehicles(user)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Gates /new-appointment: needs update on Today OR Upcoming.
const RequireCreateAppointment = ({ children }) => {
  const { user } = useAuth();
  if (!canCR(user, "today", "update") && !canCR(user, "upcoming", "update")) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AppRouter = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <NavGuardProvider>
              <Layout />
            </NavGuardProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        {/* Old routes redirect to new structure */}
        <Route path="day" element={<Navigate to="/customer-relations/appointment" replace />} />
        <Route path="month" element={<Navigate to="/customer-relations/history?tab=month" replace />} />
        <Route path="year" element={<Navigate to="/customer-relations/history?tab=year" replace />} />
        {/* Customer Relations routes */}
        <Route path="customer-relations/appointment" element={<RequirePermission page="today"><Appointment /></RequirePermission>} />
        <Route path="customer-relations/upcoming" element={<RequirePermission page="upcoming"><Upcoming /></RequirePermission>} />
        <Route path="customer-relations/history" element={<RequirePermission page="history"><History /></RequirePermission>} />
        <Route path="customer-relations/reception" element={<RequirePermission page="reception"><Reception /></RequirePermission>} />
        <Route path="data-quality" element={<RequirePermission page="data_quality"><DataQuality /></RequirePermission>} />
        <Route path="customer-relations/reception/:entryId" element={<RequirePermission page="reception"><Reception /></RequirePermission>} />
        {/* Vehicles routes — any Vehicles sub-page view */}
        <Route path="vehicles" element={<Navigate to="/vehicles/renault" replace />} />
        <Route path="vehicles/renault" element={<RequireVehicles><Vehicles /></RequireVehicles>} />
        <Route path="vehicles/renault/new" element={<RequireVehicles><VehicleForm brand="renault" mode="add" /></RequireVehicles>} />
        <Route path="vehicles/renault/:vehicleId" element={<RequireVehicles><VehicleProfile brand="renault" /></RequireVehicles>} />
        <Route path="vehicles/renault/:vehicleId/edit" element={<RequireVehicles><VehicleForm brand="renault" mode="edit" /></RequireVehicles>} />
        <Route path="vehicles/other-brands" element={<RequireVehicles><VehiclesOtherBrands /></RequireVehicles>} />
        <Route path="vehicles/other-brands/new" element={<RequireVehicles><VehicleForm brand="other" mode="add" /></RequireVehicles>} />
        <Route path="vehicles/other-brands/:vehicleId" element={<RequireVehicles><VehicleProfile brand="other" /></RequireVehicles>} />
        <Route path="vehicles/other-brands/:vehicleId/edit" element={<RequireVehicles><VehicleForm brand="other" mode="edit" /></RequireVehicles>} />
        {/* Legacy vehicles route redirect */}
        <Route path="customer-relations/vehicles" element={<Navigate to="/vehicles/renault" replace />} />
        <Route path="new-appointment" element={<RequireCreateAppointment><NewAppointment /></RequireCreateAppointment>} />
        <Route path="appointments/:id" element={<AppointmentDetail />} />
        {/* Settings routes — settings.master only */}
        <Route path="settings" element={<RequirePermission page="settings" level="master"><SettingsOverview /></RequirePermission>} />
        <Route path="other-settings" element={<RequirePermission page="settings" level="master"><OtherSettings /></RequirePermission>} />
        <Route path="branches" element={<RequirePermission page="settings" level="master"><Branches /></RequirePermission>} />
        <Route path="roles" element={<RequirePermission page="settings" level="master"><Roles /></RequirePermission>} />
        <Route path="users" element={<RequirePermission page="settings" level="master"><UserManagement /></RequirePermission>} />
        {/* Insurance module — insurance.view */}
        <Route path="module/insurance" element={<RequirePermission page="insurance"><InsurancePolicies /></RequirePermission>} />
        {/* Coming Soon module pages — gated per :module matrix key */}
        <Route path="module/:module" element={<RequireModulePermission><ComingSoon /></RequireModulePermission>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <TestModeProvider>
          <AppRouter />
        </TestModeProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
